import { useEffect, useRef, useState } from "react";
import {
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiEdit2,
  FiPlus,
  FiTrash2,
  FiUpload,
} from "react-icons/fi";

import Button from "@/components/ui/Button";
import AssetsListSaveMenu from "./AssetsListSaveMenu";
import {
  MAX_ASSETS_LIST_NAME_LENGTH,
  UNSAVED_ASSETS_LIST_LABEL,
} from "./constants";

export default function AssetsListToolbar({
  listName,
  hasLoadedList,
  entryCount,
  isDirty,
  isExpanded,
  isBusy,
  isDownloadingList,
  onToggleExpanded,
  onNewList,
  onSaveList,
  onSaveListAs,
  onRenameList,
  onLoadList,
  onDeleteList,
  onDownloadList,
}) {
  const displayedName = listName ? listName : UNSAVED_ASSETS_LIST_LABEL;

  const [isRenaming, setIsRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(listName);
  const nameInputRef = useRef(null);
  // Enter/Esc commit or cancel and then blur the input themselves — without
  // this, the resulting blur would immediately fire a second, redundant commit/cancel.
  const skipBlurRef = useRef(false);

  useEffect(() => {
    if (!isRenaming) return;
    nameInputRef.current?.focus();
    nameInputRef.current?.select();
  }, [isRenaming]);

  const startRename = () => {
    setNameDraft(listName);
    setIsRenaming(true);
  };

  const commitRename = () => {
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== listName) onRenameList({ name: trimmed });
    setIsRenaming(false);
  };

  const cancelRename = () => setIsRenaming(false);

  const handleNameKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      skipBlurRef.current = true;
      commitRename();
    } else if (event.key === "Escape") {
      event.preventDefault();
      skipBlurRef.current = true;
      cancelRename();
    }
  };

  const handleNameBlur = () => {
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      return;
    }
    cancelRename();
  };

  return (
    <div className="AssetsList__toolbar flex items-center gap-2 px-2 pt-2 pb-1 shrink-0 @[700px]:pr-10">
      <button
        id="AssetsList__toggle"
        type="button"
        title={isExpanded ? "Collapse list" : "Expand list"}
        onClick={onToggleExpanded}
        className="AssetsList__toggle shrink-0 p-1 rounded-sm text-secondary-foreground/70 hover:text-foreground hover:bg-secondary/60 cursor-pointer">
        {isExpanded ? (
          <FiChevronDown className="w-4 h-4" />
        ) : (
          <FiChevronUp className="w-4 h-4" />
        )}
      </button>

      {isRenaming ? (
        <input
          id="AssetsList__nameInput"
          ref={nameInputRef}
          type="text"
          value={nameDraft}
          maxLength={MAX_ASSETS_LIST_NAME_LENGTH}
          onChange={(event) => setNameDraft(event.target.value)}
          onKeyDown={handleNameKeyDown}
          onBlur={handleNameBlur}
          className="AssetsList__nameInput text-xs font-medium bg-card rounded-sm px-1 py-0.5 w-[220px] border-0 outline-none ring-1 ring-primary"
        />
      ) : (
        <div className="AssetsList__nameGroup relative flex items-center gap-1 shrink-0 group/name">
          <span
            id="AssetsList__name"
            title={displayedName}
            className="AssetsList__name text-xs font-medium truncate max-w-[220px]">
            {displayedName}
            {isDirty && <span className="text-primary"> *</span>}
          </span>
          <button
            id="AssetsList__rename"
            type="button"
            title="Rename list"
            disabled={isBusy}
            onClick={startRename}
            className="AssetsList__rename hidden group-hover/name:flex items-center justify-center p-0.5 rounded-sm text-secondary-foreground/70 hover:text-foreground hover:bg-secondary/60 cursor-pointer shrink-0">
            <FiEdit2 className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* {entryCount > 0 && (
        <span className="AssetsList__count text-[11px] text-secondary-foreground/60 shrink-0">
          {entryCount} {entryCount === 1 ? "Asset" : "Assets"}
        </span>
      )} */}

      {isExpanded && (
        <div className="AssetsList__actions flex items-center gap-2 ml-auto shrink-0">
          {hasLoadedList && (
            <>
              <Button
                id="AssetsList__delete"
                type="danger-ghost"
                size="sm"
                disabled={isBusy}
                icon={<FiTrash2 className="w-3.5 h-3.5" />}
                onClick={onDeleteList}>
                Delete
              </Button>
              <ToolbarSeparator />
            </>
          )}

          {/* One archive: the assets and the edit decision list together. The
              edl is generated from the stored list, so an unsaved or dirty
              list gets prompted to save first rather than shipping a
              mismatched edit — see AssetsListContainer's handleDownloadList. */}
          <Button
            id="AssetsList__download"
            type="secondary"
            size="sm"
            disabled={isBusy || entryCount === 0 || isDownloadingList}
            icon={<FiDownload className="w-3.5 h-3.5" />}
            onClick={onDownloadList}>
            {isDownloadingList ? "Downloading…" : "Download"}
          </Button>

          <ToolbarSeparator />

          <AssetsListSaveMenu
            isBusy={isBusy}
            isSaveDisabled={isBusy || entryCount === 0 || !isDirty}
            isSaveAsDisabled={isBusy || entryCount === 0}
            onSaveList={onSaveList}
            onSaveListAs={onSaveListAs}
          />
          <Button
            id="AssetsList__load"
            type="secondary"
            size="sm"
            disabled={isBusy}
            icon={<FiUpload className="w-3.5 h-3.5" />}
            onClick={onLoadList}>
            Load
          </Button>
          <Button
            id="AssetsList__new"
            type="secondary"
            size="sm"
            disabled={isBusy}
            icon={<FiPlus className="w-3.5 h-3.5" />}
            onClick={onNewList}>
            New
          </Button>
        </div>
      )}
    </div>
  );
}

function ToolbarSeparator() {
  return (
    <span
      aria-hidden="true"
      className="AssetsList__actionsSeparator h-4 w-px bg-border shrink-0"
    />
  );
}
